import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";

import { putRequestCase, postRequestCase, deleteRequestCase } from "@/stores/caseStore/backendApi";
import { useCaseStore } from "@/stores/caseStore/0_caseStore";
import { withQuestionnaireSource } from "@/stores/caseStore/questionnaireSource";
import { useSnackbarStore } from "@/stores/snackbarStore";
import * as d from "@/utils/dataUtils";

// model

export const defaultVehicle = {
  // vehicle
  category: null,
  subcategory: null,
  subcategoryOther: "",

  manufacturer: null,
  model: "",
  color: "",
  yearOfconstruction: null,
  firstRegistration: null,
  serviceInAuthorizedWorkshop: null,
  serviceProof: null,
  financingType: null,
  hasTrailer: null,

  registration: null,
  licensePlate: "",
  licenseCountry: null,

  insuranceAvailable: null,
  insuranceType: null,
  insuranceName: "",
  insuranceDeductible: null,
  insuranceDeductibleFull: null,
  insuranceDeductiblePartial: null,
  insurancePolicyNumber: "",
  insuranceCaseNumber: "",

  previousDamage: null,
  previousDamageSameArea: null,
  previousDamageDescription: "",
  previousDamageInvoice: null,
  previousDamageInsuranceClaim: null,
  previousDamageInvoiceSubmitted: null,

  existingDamage: null,
  existingDamageSameArea: null,
  existingDamageDescription: "",
  existingDamageInvoice: null,
  existingDamageInsuranceClaim: null,
  existingDamageInvoiceSubmitted: null,

  foreignProtectionInsuranceAvailable: null,
  foreignProtectionInsuranceName: "",
  foreignProtectionInsuranceDeductible: null,
  foreignProtectionInsurancePolicyNumber: "",
  foreignProtectionInsuranceCaseNumber: "",

  protectionBriefInsuranceAvailable: null,
  protectionBriefInsuranceName: "",
  protectionBriefInsuranceDeductible: null,
  protectionBriefInsurancePolicyNumber: "",
  protectionBriefInsuranceCaseNumber: "",

  gapInsuranceAvailable: null,
  gapInsuranceName: "",
  gapInsuranceDeductible: null,
  gapInsurancePolicyNumber: "",
  gapInsuranceCaseNumber: "",

  financingCompany: "",
  financingContractNumber: "",

  remarksVehicle: "",

  // gutachten extended
  besteuerung: null,
  differenzbesteuerung: null,
  wertverbesserungBrutto: null,
  wertminderungErmitteltNach: null,
  kostenNotreparaturBrutto: null,
  reparaturdauerArbeitstage: null,
  wiederbeschaffungsdauerTage: null,
  basisMietwagenklasse: null,

  //settlement
  vat: null,
  vatShare: null,
  settlement: null,
  calculationbasis: null,

  repair: null,
  repairType: null,
  repairWorkshop: "",
  repairStarted: null,
  repairDone: null,
  repairFrom: null,
  repairTo: null,
  repairScheduled: null,
  repairScheduledDate: null,
  repairPlannedDate: null,
  repairNote: "",

  continueUsingAccidentVehicle: null,
  moreThanSixMonthsUse: null,

  sellAccidentVehicle: null,
  soldAlready: null,
  soldFor: null,
  soldForEquals: null,
  soldContract: null,

  replaced: null,
  replacementDone: null,
  replacedFor: null,
  replacedVat: null,

  costEstimate: null,
  costEstimateAccordingToClient: "",
  expertHired: null,
  expertAccordingToClient: "",
  expertReport: null,
  expertReportNumberAccordingToClient: "",

  vehicleSafeAfterAccident: null,
  repairEmergencyPossible: null,
  madeSafe: null,
  madeSafePlanned: null,
  emergencyRepairFrom: null,
  emergencyRepairUntil: null,

  currentUse: null,
  rentalCar: null,
  rentalCarAlready: null,
  rentalCarDone: null,
  rentalCarIntermediary: null,
  rentalCarCompany: "",
  rentalCarAlreadyFrom: null,
  rentalCarAlreadyTo: null,
  rentalCarFuture: null,
  rentalCarFutureFrom: null,
  rentalCarFutureTo: null,
  lossOfUse: null,
  holdingCosts: null,
  alternateVehiclesAvailable: null,
  alternateVehicleDetails: "",
  alternateVehiclesThirdPartyLoss: null,
  vehiclesHeld: null,
  heldVehicleDetails: "",

  contactInsurance: null,
  contactInsuranceHow: null,
  contactInsuranceResponsible: null,
  contactInsuranceResponsibleAmount: null,
  contactInsuranceSettlement: null,
  contactInsuranceSettlementContent: "",
  contactInsuranceInspectionReport: null,
  contactInsuranceRentalCarOffer: null,
  contactInsuranceReferenceRepairShop: null,
  contactInsuranceOffer: null,

  remarksSettlement: "",
};

// CRUD

/**
 * Fehler werden per Snackbar gemeldet und – wie bisher – NICHT weitergereicht.
 * Mit `{ rethrow: true }` zusätzlich weiterreichen (Fragebogen-Hülle: zeigt
 * dann „Nicht gespeichert“ statt „Gespeichert“).
 */
export async function saveVehicles(options: { rethrow?: boolean } = {}): Promise<void> {
  const store = useCaseStore();
  const snackbar = useSnackbarStore();

  if (store.savingVehicles) return;
  store.savingVehicles = true;

  try {
    const current = store.vehicles.map(prepareVehicleForSave);
    const previous = store.lastSavedVehicles.map(prepareVehicleForSave);

    if (isEqual(current, previous)) return;

    for (const vehicle of current) {
      await putRequestCase(withQuestionnaireSource(`/protected/cases/${store.caseData.id}/vehicles/${vehicle.id}`, store.questionnaireView), vehicle);
    }

    store.lastSavedVehicles = cloneDeep(store.vehicles);
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : (error as any)?.message || "Fehler beim Speichern";
    snackbar.showSnackbar({ message: msg, type: "error" });
    if (options.rethrow) throw error;
  } finally {
    store.savingVehicles = false;
  }
}

export async function addVehicle(): Promise<void> {
  const store = useCaseStore();
  const response = await postRequestCase(`/protected/cases/${store.caseData.id}/vehicles`);
  store.vehicles.push(prepareVehicleForLoad(response));
  store.lastSavedVehicles = cloneDeep(store.vehicles);
}

export async function deleteVehicle(idVehicle: string): Promise<void> {
  const store = useCaseStore();
  const index = store.vehicles.findIndex((v) => String(v.id) === String(idVehicle));
  if (index === -1) return;

  // Kundenfeedback 26.10.2026 (VIII): Löschen aus dem Fragebogen – ein
  // Eintrag mit Inhalt bleibt in der Akte, bis die Kanzlei entscheidet.
  await deleteRequestCase(withQuestionnaireSource(`/protected/cases/${store.caseData.id}/vehicles/${idVehicle}`, store.questionnaireView));
  store.vehicles.splice(index, 1);
  store.lastSavedVehicles = cloneDeep(store.vehicles);
}

// data helper

type Vehicle = Record<string, any>;

export function prepareVehicleForLoad(data: Vehicle): Vehicle {
  return {
    ...data,

    // vehicle
    category: d.loadSelect(data.category),
    subcategory: d.loadSelect(data.subcategory),

    yearOfConstruction: d.loadInteger(data.yearOfConstruction),
    firstRegistration: d.loadDate(data.firstRegistration),

    serviceInAuthorizedWorkshop: d.loadTristate(data.serviceInAuthorizedWorkshop),

    financingType: d.loadSelect(data.financingType),
    hasTrailer: d.loadTristate(data.hasTrailer),

    registration: d.loadTristate(data.registration),
    licenseCountry: d.loadSelect(data.licenseCountry),

    insuranceAvailable: d.loadTristate(data.insuranceAvailable),
    insuranceDeductible: d.loadDecimal(data.insuranceDeductible),
    insuranceDeductibleFull: d.loadDecimal(data.insuranceDeductibleFull),
    insuranceDeductiblePartial: d.loadDecimal(data.insuranceDeductiblePartial),

    previousDamage: d.loadTristate(data.previousDamage),
    previousDamageSameArea: d.loadTristate(data.previousDamageSameArea),
    previousDamageInvoice: d.loadTristate(data.previousDamageInvoice),
    previousDamageInsuranceClaim: d.loadTristate(data.previousDamageInsuranceClaim),
    previousDamageInvoiceSubmitted: d.loadTristate(data.previousDamageInvoiceSubmitted),

    existingDamage: d.loadTristate(data.existingDamage),
    existingDamageSameArea: d.loadTristate(data.existingDamageSameArea),
    existingDamageInvoice: d.loadTristate(data.existingDamageInvoice),
    existingDamageInsuranceClaim: d.loadTristate(data.existingDamageInsuranceClaim),
    existingDamageInvoiceSubmitted: d.loadTristate(data.existingDamageInvoiceSubmitted),

    foreignProtectionInsuranceAvailable: d.loadTristate(data.foreignProtectionInsuranceAvailable),
    foreignProtectionInsuranceDeductible: d.loadDecimal(data.foreignProtectionInsuranceDeductible),

    protectionBriefInsuranceAvailable: d.loadTristate(data.protectionBriefInsuranceAvailable),
    protectionBriefInsuranceDeductible: d.loadDecimal(data.protectionBriefInsuranceDeductible),

    gapInsuranceAvailable: d.loadTristate(data.gapInsuranceAvailable),
    gapInsuranceDeductible: d.loadDecimal(data.gapInsuranceDeductible),

    // gutachten extended
    besteuerung: d.loadSelect(data.besteuerung),
    differenzbesteuerung: d.loadDecimal(data.differenzbesteuerung),
    wertverbesserungBrutto: d.loadDecimal(data.wertverbesserungBrutto),
    wertminderungErmitteltNach: d.loadSelect(data.wertminderungErmitteltNach),
    kostenNotreparaturBrutto: d.loadDecimal(data.kostenNotreparaturBrutto),
    reparaturdauerArbeitstage: d.loadInteger(data.reparaturdauerArbeitstage),
    wiederbeschaffungsdauerTage: d.loadInteger(data.wiederbeschaffungsdauerTage),
    basisMietwagenklasse: d.loadSelect(data.basisMietwagenklasse),

    //settlement
    vat: d.loadTristate(data.vat),
    vatShare: d.loadDecimal(data.vatShare),

    settlement: d.loadSelect(data.settlement),
    calculationbasis: d.loadSelect(data.calculationbasis),

    repair: d.loadTristate(data.repair),
    repairType: d.loadSelect(data.repairType),
    repairStarted: d.loadTristate(data.repairStarted),
    repairDone: d.loadTristate(data.repairDone),
    repairFrom: d.loadDate(data.repairFrom),
    repairTo: d.loadDate(data.repairTo),
    repairScheduled: d.loadTristate(data.repairScheduled),
    repairScheduledDate: d.loadDate(data.repairScheduledDate),
    repairPlannedDate: d.loadDate(data.repairPlannedDate),

    continueUsingAccidentVehicle: d.loadTristate(data.continueUsingAccidentVehicle),
    moreThanSixMonthsUse: d.loadTristate(data.moreThanSixMonthsUse),

    sellAccidentVehicle: d.loadTristate(data.sellAccidentVehicle),
    soldAlready: d.loadTristate(data.soldAlready),
    soldFor: d.loadDecimal(data.soldFor),
    soldForEquals: d.loadSelect(data.soldForEquals),
    soldContract: d.loadTristate(data.soldContract),

    replaced: d.loadTristate(data.replaced),
    replacementDone: d.loadTristate(data.replacementDone),
    replacedFor: d.loadDecimal(data.replacedFor),
    replacedVat: d.loadSelect(data.replacedVat),

    costEstimate: d.loadTristate(data.costEstimate),
    expertHired: d.loadTristate(data.expertHired),
    expertReport: d.loadTristate(data.expertReport),

    vehicleSafeAfterAccident: d.loadTristate(data.vehicleSafeAfterAccident),
    repairEmergencyPossible: d.loadTristate(data.repairEmergencyPossible),
    madeSafe: d.loadTristate(data.madeSafe),
    madeSafePlanned: d.loadTristate(data.madeSafePlanned),
    emergencyRepairFrom: d.loadDate(data.emergencyRepairFrom),
    emergencyRepairUntil: d.loadDate(data.emergencyRepairUntil),

    currentUse: d.loadTristate(data.currentUse),
    rentalCar: d.loadTristate(data.rentalCar),
    rentalCarAlready: d.loadTristate(data.rentalCarAlready),
    rentalCarDone: d.loadTristate(data.rentalCarDone),
    rentalCarIntermediary: d.loadSelect(data.rentalCarIntermediary),
    rentalCarAlreadyFrom: d.loadDate(data.rentalCarAlreadyFrom),
    rentalCarAlreadyTo: d.loadDate(data.rentalCarAlreadyTo),
    rentalCarFuture: d.loadTristate(data.rentalCarFuture),
    rentalCarFutureFrom: d.loadDate(data.rentalCarFutureFrom),
    rentalCarFutureTo: d.loadDate(data.rentalCarFutureTo),
    lossOfUse: d.loadTristate(data.lossOfUse),
    holdingCosts: d.loadTristate(data.holdingCosts),
    alternateVehiclesAvailable: d.loadTristate(data.alternateVehiclesAvailable),
    alternateVehiclesThirdPartyLoss: d.loadTristate(data.alternateVehiclesThirdPartyLoss),
    vehiclesHeld: d.loadTristate(data.vehiclesHeld),

    contactInsurance: d.loadTristate(data.contactInsurance),
    contactInsuranceHow: d.loadSelect(data.contactInsuranceHow),
    contactInsuranceResponsible: d.loadSelect(data.contactInsuranceResponsible),
    contactInsuranceResponsibleAmount: d.loadDecimal(data.contactInsuranceResponsibleAmount),
    contactInsuranceSettlement: d.loadTristate(data.contactInsuranceSettlement),
    contactInsuranceInspectionReport: d.loadTristate(data.contactInsuranceInspectionReport),
    contactInsuranceRentalCarOffer: d.loadTristate(data.contactInsuranceRentalCarOffer),
    contactInsuranceReferenceRepairShop: d.loadTristate(data.contactInsuranceReferenceRepairShop),
    contactInsuranceOffer: d.loadTristate(data.contactInsuranceOffer),
  };
}

export function prepareVehicleForSave(data: Vehicle): Vehicle {
  return {
    ...data,

    // vehicle
    category: d.saveSelect(data.category),
    subcategory: d.saveSelect(data.subcategory),

    yearOfConstruction: d.saveInteger(data.yearOfConstruction),
    firstRegistration: d.saveDate(data.firstRegistration),

    serviceInAuthorizedWorkshop: d.saveTristate(data.serviceInAuthorizedWorkshop),

    financingType: d.saveSelect(data.financingType),
    hasTrailer: d.saveTristate(data.hasTrailer),

    registration: d.saveTristate(data.registration),
    licenseCountry: d.saveSelect(data.licenseCountry),

    insuranceAvailable: d.saveTristate(data.insuranceAvailable),
    insuranceDeductible: d.saveFloat(data.insuranceDeductible),
    insuranceDeductibleFull: d.saveFloat(data.insuranceDeductibleFull),
    insuranceDeductiblePartial: d.saveFloat(data.insuranceDeductiblePartial),

    previousDamage: d.saveTristate(data.previousDamage),
    previousDamageSameArea: d.saveTristate(data.previousDamageSameArea),
    previousDamageInvoice: d.saveTristate(data.previousDamageInvoice),
    previousDamageInsuranceClaim: d.saveTristate(data.previousDamageInsuranceClaim),
    previousDamageInvoiceSubmitted: d.saveTristate(data.previousDamageInvoiceSubmitted),

    existingDamage: d.saveTristate(data.existingDamage),
    existingDamageSameArea: d.saveTristate(data.existingDamageSameArea),
    existingDamageInvoice: d.saveTristate(data.existingDamageInvoice),
    existingDamageInsuranceClaim: d.saveTristate(data.existingDamageInsuranceClaim),
    existingDamageInvoiceSubmitted: d.saveTristate(data.existingDamageInvoiceSubmitted),

    foreignProtectionInsuranceAvailable: d.saveTristate(data.foreignProtectionInsuranceAvailable),
    foreignProtectionInsuranceDeductible: d.saveFloat(data.foreignProtectionInsuranceDeductible),

    protectionBriefInsuranceAvailable: d.saveTristate(data.protectionBriefInsuranceAvailable),
    protectionBriefInsuranceDeductible: d.saveFloat(data.protectionBriefInsuranceDeductible),

    gapInsuranceAvailable: d.saveTristate(data.gapInsuranceAvailable),
    gapInsuranceDeductible: d.saveFloat(data.gapInsuranceDeductible),

    // gutachten extended
    besteuerung: d.saveSelect(data.besteuerung),
    differenzbesteuerung: d.saveFloat(data.differenzbesteuerung),
    wertverbesserungBrutto: d.saveFloat(data.wertverbesserungBrutto),
    wertminderungErmitteltNach: d.saveSelect(data.wertminderungErmitteltNach),
    kostenNotreparaturBrutto: d.saveFloat(data.kostenNotreparaturBrutto),
    reparaturdauerArbeitstage: d.saveInteger(data.reparaturdauerArbeitstage),
    wiederbeschaffungsdauerTage: d.saveInteger(data.wiederbeschaffungsdauerTage),
    basisMietwagenklasse: d.saveSelect(data.basisMietwagenklasse),

    //settlement
    vat: d.saveTristate(data.vat),
    vatShare: d.saveFloat(data.vatShare),

    settlement: d.saveSelect(data.settlement),
    calculationbasis: d.saveSelect(data.calculationbasis),

    repair: d.saveTristate(data.repair),
    repairType: d.saveSelect(data.repairType),
    repairStarted: d.saveTristate(data.repairStarted),
    repairDone: d.saveTristate(data.repairDone),
    repairFrom: d.saveDate(data.repairFrom),
    repairTo: d.saveDate(data.repairTo),
    repairScheduled: d.saveTristate(data.repairScheduled),
    repairScheduledDate: d.saveDate(data.repairScheduledDate),
    repairPlannedDate: d.saveDate(data.repairPlannedDate),

    continueUsingAccidentVehicle: d.saveTristate(data.continueUsingAccidentVehicle),
    moreThanSixMonthsUse: d.saveTristate(data.moreThanSixMonthsUse),

    sellAccidentVehicle: d.saveTristate(data.sellAccidentVehicle),
    soldAlready: d.saveTristate(data.soldAlready),
    soldFor: d.saveFloat(data.soldFor),
    soldForEquals: d.saveSelect(data.soldForEquals),
    soldContract: d.saveTristate(data.soldContract),

    replaced: d.saveTristate(data.replaced),
    replacementDone: d.saveTristate(data.replacementDone),
    replacedFor: d.saveFloat(data.replacedFor),
    replacedVat: d.saveSelect(data.replacedVat),

    costEstimate: d.saveTristate(data.costEstimate),
    expertHired: d.saveTristate(data.expertHired),
    expertReport: d.saveTristate(data.expertReport),

    vehicleSafeAfterAccident: d.saveTristate(data.vehicleSafeAfterAccident),
    repairEmergencyPossible: d.saveTristate(data.repairEmergencyPossible),
    madeSafe: d.saveTristate(data.madeSafe),
    madeSafePlanned: d.saveTristate(data.madeSafePlanned),
    emergencyRepairFrom: d.saveDate(data.emergencyRepairFrom),
    emergencyRepairUntil: d.saveDate(data.emergencyRepairUntil),

    currentUse: d.saveTristate(data.currentUse),
    rentalCar: d.saveTristate(data.rentalCar),
    rentalCarAlready: d.saveTristate(data.rentalCarAlready),
    rentalCarDone: d.saveTristate(data.rentalCarDone),
    rentalCarIntermediary: d.saveSelect(data.rentalCarIntermediary),
    rentalCarAlreadyFrom: d.saveDate(data.rentalCarAlreadyFrom),
    rentalCarAlreadyTo: d.saveDate(data.rentalCarAlreadyTo),
    rentalCarFuture: d.saveTristate(data.rentalCarFuture),
    rentalCarFutureFrom: d.saveDate(data.rentalCarFutureFrom),
    rentalCarFutureTo: d.saveDate(data.rentalCarFutureTo),
    lossOfUse: d.saveTristate(data.lossOfUse),
    holdingCosts: d.saveTristate(data.holdingCosts),
    alternateVehiclesAvailable: d.saveTristate(data.alternateVehiclesAvailable),
    alternateVehiclesThirdPartyLoss: d.saveTristate(data.alternateVehiclesThirdPartyLoss),
    vehiclesHeld: d.saveTristate(data.vehiclesHeld),

    contactInsurance: d.saveTristate(data.contactInsurance),
    contactInsuranceHow: d.saveSelect(data.contactInsuranceHow),
    contactInsuranceResponsible: d.saveSelect(data.contactInsuranceResponsible),
    contactInsuranceResponsibleAmount: d.saveInteger(data.contactInsuranceResponsibleAmount),
    contactInsuranceSettlement: d.saveTristate(data.contactInsuranceSettlement),
    contactInsuranceInspectionReport: d.saveTristate(data.contactInsuranceInspectionReport),
    contactInsuranceRentalCarOffer: d.saveTristate(data.contactInsuranceRentalCarOffer),
    contactInsuranceReferenceRepairShop: d.saveTristate(data.contactInsuranceReferenceRepairShop),
    contactInsuranceOffer: d.saveTristate(data.contactInsuranceOffer),
  };
}
